import { createApp, defineAsyncComponent, watch } from 'vue'
import { envOverrides } from '@sennder/shell-utilities'

import router from './router'
import { logger } from '@/services/logger/loggers'
import { ENV, LAUNCH_DARKLY_KEY } from './common/config'
import { loadUserData } from '@/store/loadUserData'
import { registerRoutes } from './router/routes'
import { auth, loadAuthRemoteFunction } from '@/modules/auth'
import { startTracking } from './services/tracking'
import { translationProvider } from '@/services/translationProvider'
import { microfrontends } from './config/microfrontends'
import { Shell } from '@sennder/microfrontend-registry'
import { SharedDataType } from '@sennder/senn-node-microfrontend-interfaces'
import { getStateData, setStoreLegacyFlags } from '@/store'
import { getMfContext, shellLogLevel, shell } from '@/config/registry'
import { initSessionReplay, stopSessionReplay } from '@/services/analytics'
import {
  getExperimentalConfig,
  injectRuntimeConfig,
} from '@/config/getExperimentalConfig'
import { i18n } from '@/services/i18n'
import { initActivityFeedProvider } from '@/services/activity-feed'
import { preloadMicrofrontend } from '@/config/preloadMicrofrontend'
import { featureFlag } from '@/services/featureFlagProvider'

const bootPromises: Promise<void>[] = []
let userDataPromise: Promise<void> | undefined = undefined

async function start() {
  // TODO: if required, check for user consent before starting tracking
  startTracking()

  try {
    window.onerror = function (message, url, line, column, error) {
      logger.error('[tms - global error]', { error })
    }

    shell.value = new Shell(
      SharedDataType.SENN_TMS,
      getMfContext,
      ENV,
      'none',
      shellLogLevel
    )

    const authFnPromise = loadAuthRemoteFunction()

    if (ENV === 'dev') {
      const config = getExperimentalConfig()

      if (config) {
        injectRuntimeConfig(config)
      }

      envOverrides.load({
        legacyModules: Object.values(microfrontends),
        logger,
      })
    }

    if (!LAUNCH_DARKLY_KEY) {
      throw new Error('LAUNCH_DARKLY_KEY not found')
    }

    bootPromises.push(
      featureFlag.init(LAUNCH_DARKLY_KEY, void 0, {
        // TODO: Remove this override once all micro-frontends are migrated to the new feature flag provider
        sendEventsOnlyForVariation: false,
      }),
      translationProvider.initialize()
    )

    await authFnPromise
    if (await auth.value?.isAuthenticated()) {
      bootPromises.push(preloadMicrofrontend(window.location.pathname))
      userDataPromise = loadUserData().then(async () => {
        // Init activityfeed is relying on profile to be populated
        await initActivityFeedProvider()
      })
    }
  } catch (error) {
    logger.error(`[tms] shell init`, { error })
  } finally {
    registerRoutes()

    await userDataPromise
    const app = createApp(defineAsyncComponent(() => import('./App.vue')))
      .use(router)
      .use(i18n)

    const { unmount } = app

    const stopWatchData = watch(getStateData, () => {
      shell.value?.onSharedDataChange()
    })

    // This should be the only watchAllFlags call in the application to avoid noisy logging to LaunchDarkly. Otherwise, each watchAllFlags will report another set of feature flag evaluations.
    const stopWatchLegacyFlags =
      featureFlag.devFunctions.watchAllFlags(setStoreLegacyFlags)

    const stopWatchAmplitudeReplayFlag = featureFlag.watchFeatureFlag(
      'ENABLE_AMPLITUDE_SESSION_REPLAY',
      (enable: boolean) => {
        if (enable) {
          initSessionReplay()
        } else {
          stopSessionReplay()
        }
      }
    )

    app.unmount = (...args) => {
      unmount(...args)
      stopWatchData()
      stopWatchAmplitudeReplayFlag()
      stopWatchLegacyFlags()
      shell.value?.destroy()
    }

    await Promise.allSettled(bootPromises)
    await router.isReady()
    app.mount('#app')
  }
}

start()
